import { useQuasar } from "quasar";

import { post, postContact, postCustomContact } from "src/api/chat.js";

import AddContactDialog from "src/modules/chat/components/AddContactDialog.vue";

/**
 * Composable for managing chat contacts and conversations
 * Handles contact selection, conversation creation, and contact display logic
 */
export function useChatContactUtils() {
  const $q = useQuasar();
  const { normalizePhone } = useChatUtils();
  const { getConversationBySid } = useChatStore();
  const { getExistingContact } = useChatMessageUtils();

  const createdContact = ref(null);

  const { openFloatingChat } = inject("floating-chats", {});

  /**
   * Open add contact dialog and handle contact creation
   * Reusable function for showing add contact dialog
   * @param {Object} $q - Quasar instance
   * @param {Object} AddContactDialog - AddContactDialog component
   * @param {Object} options - Dialog options
   * @param {string} options.contactNumber - Phone number to pre-fill
   * @param {string} options.businessName - Business name to pre-fill
   * @param {Function} options.onContactCreated - Callback when contact is created
   * @returns {void}
   */
  const openAddContactDialog = ({
    contactNumber = "",
    businessName = "",
    onContactCreated = null,
  } = {}) => {
    try {
      $q.dialog({
        component: AddContactDialog,
        componentProps: {
          contactNumber,
          businessName,
        },
      }).onOk(async (contact) => {
        createdContact.value = contact;

        if (onContactCreated) {
          await onContactCreated(contact);
        }

        if (openFloatingChat) {
          openFloatingConversation(contact);
        }
      });
    } catch (error) {
      console.error("Failed to open add contact dialog:", error);
    }
  };

  /**
   * Get contact's full name with fallback logic
   * Priority: Full Name > First Name > Business Name > Phone > "Unknown"
   * @param {Object} contact - Contact object
   * @returns {string} Full name for the contact
   */
  const getContactName = (contact) => {
    const firstName = contact.firstName || "";
    const lastName = contact.lastName || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    if (firstName) {
      return firstName;
    }

    if (contact.businessName) {
      return contact.businessName;
    }

    return contact.phone || "Unknown Contact";
  };

  /**
   * Filter contacts based on search query
   * Searches across firstName, lastName, phone, and businessName
   * @param {Array} contacts - Array of contact objects
   * @param {string} query - Search query string
   * @returns {Array} Filtered array of contacts
   */
  const filterContacts = (contacts, query) => {
    if (!query) {
      return contacts;
    }

    const searchQuery = query.toLowerCase().trim();

    return unref(contacts).filter((contact) => {
      const {
        firstName = "",
        lastName = "",
        phone = "",
        businessName = "",
      } = unref(contact);

      const firstNameLower = firstName.toLowerCase();
      const lastNameLower = lastName.toLowerCase();
      const phoneLower = String(phone).toLowerCase();
      const businessNameLower = businessName.toLowerCase();
      const fullName = `${firstNameLower} ${lastNameLower}`;

      return (
        firstNameLower.includes(searchQuery) ||
        lastNameLower.includes(searchQuery) ||
        fullName.includes(searchQuery) ||
        phoneLower.includes(searchQuery) ||
        businessNameLower.includes(searchQuery)
      );
    });
  };

  /**
   * Create a custom contact for onboarding
   * @param {Object} contact - Contact object with firstName, lastName, phone, email
   * @param {Object} linkedUser - User object with _id and role to link contact to
   * @returns {Promise<Object>} Created custom contact object
   */
  const createCustomContact = async (contact, linked_to) => {
    try {
      const {
        firstName = "",
        lastName = "",
        phone = "",
        email = "",
      } = unref(contact);

      const { _id } = unref(linked_to)?.[0] || {};

      const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

      const normalizedPhone = normalizePhone(phone, { includeIsdCode: false });

      let payload = {
        name: fullName,
        phone: normalizedPhone,
        email: email,
      };

      if (_id) {
        payload = {
          ...payload,
          user_id: _id,
          linked_to: unref(linked_to),
        };
      }

      const response = await postCustomContact(payload);

      return response.data?.data;
    } catch (err) {
      console.error("Failed to create custom contact:", err);

      throw err;
    }
  };

  const creatingConversation = ref(false);

  /**
   * Create conversation contact
   * @param {Object} contact - Core contact object
   * @param {Array} linkedTo - Array of linked entities (supports both linkedTo and linked_to)
   * @returns {Promise<Object>} Created contact object
   */
  const createConversationContact = async (contact, linkedTo) => {
    try {
      contact = unref(contact);

      linkedTo = unref(linkedTo);

      let {
        name = "",
        firstName = "",
        lastName = "",
        businessName = "",
        shop_name = "",
        gstNumber = "",
        gstin = "",
        role = "",
        phone,
      } = contact;

      const normalizedPhone = normalizePhone(phone);

      // Add business name from linked entity if available
      const linkedBusinessName = unref(linkedTo)?.[0]?.businessName;

      if (linkedBusinessName) {
        businessName = linkedBusinessName;
      }

      const contactData = {
        firstName: firstName || name,
        lastName,
        phone: normalizedPhone,
        businessName: businessName || shop_name,
        gstNumber: gstNumber || gstin,
        role,
      };

      const payload = {
        ...contactData,
        attributes: contactData,
      };

      if (linkedTo) {
        payload.linked_to = linkedTo;
      }

      const response = await postContact(payload);

      return response.data?.data;
    } catch (error) {
      console.error("Failed to create conversation contact:", error);
    }
  };

  /**
   * Get or create conversation contact for core contact
   * @param {Object} coreContact - Core contact object with phone, name, shop_name, etc.
   * @returns {Promise<Object|null>} Conversation contact object or null on failure
   */
  const getOrCreateConversationContact = async (coreContact) => {
    try {
      creatingConversation.value = true;

      const { phone } = unref(coreContact);

      if (!phone) {
        console.warn("Contact has no phone number");

        return null;
      }

      try {
        const normalizedPhone = normalizePhone(phone);

        // Step 1: Check if contact exists in chat system using shared utility
        let { exists, userData: conversationContact } =
          await getExistingContact(normalizedPhone);

        // Step 2: If contact doesn't exist, create it
        if (!exists) {
          conversationContact = await createConversationContact(coreContact);
        }

        createdContact.value = conversationContact;

        return conversationContact;
      } catch (error) {
        console.error("Failed to get or create conversation contact:", error);

        return null;
      }
    } catch (error) {
      console.error("Error in getOrCreateConversationContact:", error);
    } finally {
      creatingConversation.value = false;
    }
  };

  /**
   * Create a new conversation with a contact
   * @param {Object} contact - Contact object with phone, firstName, lastName, etc.
   * @returns {Promise<Object>} Object with conversation and chat data
   */
  const createConversation = async (contact) => {
    creatingConversation.value = true;

    const {
      firstName = "",
      lastName = "",
      phone = "",
      businessName = "",
      gstNumber = "",
      panNumber = "",
      role = "",
      uniqueConversationName = "",
    } = contact;

    const normalizedPhone = normalizePhone(phone);

    const response = await post({
      payload: {
        firstName,
        lastName,
        phone: normalizedPhone,
        businessName,
        gstNumber,
        panNumber,
        attributes: {
          firstName,
          lastName,
          phone: normalizedPhone,
          businessName,
          gstNumber,
          panNumber,
          role,
        },
      },
      options: {
        params: {
          showMessage: false,
          uniqueConversationName,
        },
      },
    });

    const { conversationSid } = response.data?.data || {};

    let conversation = null;

    if (conversationSid) {
      conversation = await getConversationBySid(conversationSid);
    }

    creatingConversation.value = false;

    return conversation;
  };

  /**
   * Get or create a conversation with a contact
   * Returns existing conversation if found, otherwise creates a new one
   * @param {Object} contact - Contact object
   * @returns {Promise<Object>} Object with conversation and chat data
   */
  const getOrCreateConversation = async (contact) => {
    // Try to get existing conversation if conversationSid exists
    if (contact?.conversationSid) {
      try {
        const existingConversation = await getConversationBySid(
          contact.conversationSid,
        );

        if (existingConversation) {
          return existingConversation;
        }
      } catch (error) {
        // If conversation doesn't exist or is inaccessible, create a new one
        console.warn(
          "Failed to get existing conversation, creating new one:",
          error,
        );
      }
    }

    return await createConversation(contact);
  };

  /**
   * Handle contact selection with different behaviors based on context
   * @param {Object} options.contact - The selected contact
   * @returns {Promise<void>}
   */

  const openFloatingConversation = async (contact) => {
    try {
      if (contact?.isPOContact) {
        contact = await getOrCreateConversationContact(contact);
      }

      const conversation = await getOrCreateConversation(contact);

      // Replace contact list with conversation
      openFloatingChat(conversation);
    } catch (error) {
      console.error("Failed to handle contact selection:", error);

      throw error;
    }
  };

  provide("chat-contact-utils", {
    createdContact,
    creatingConversation,

    openAddContactDialog,

    getContactName,

    filterContacts,

    createConversationContact,

    createConversation,
    getOrCreateConversation,
    openFloatingConversation,
    createCustomContact,
  });

  return {
    createdContact,
    creatingConversation,

    // Convenience helpers
    openAddContactDialog,

    // Display utilities
    getContactName,

    // Filtering
    filterContacts,

    // Contact management
    createConversationContact,
    getOrCreateConversationContact,
    createCustomContact,

    // Conversation management
    createConversation,
    getOrCreateConversation,
    openFloatingConversation,
  };
}
